import { useState } from 'react';
import moment from 'moment-timezone';
import {
  Button,
  Grid,
  Chip,
  Box,
  Tooltip,
  useTheme,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import References from './References';
import { useStyles } from './style';
import { getSlotsRange, capitalize, mobileScreen } from '../../utils/helpers';
import DoctariModal from '../Modal';
import { APPOINTMENT, APPOINTMENT_LIGHT } from '../../utils/urls';
import EditScheduleModal from '../../screens/agenda/EditScheduleModal';

const SCHEDULE_TYPE = {
  internal: 1,
  online: 2,
  faceToFace: 3,
};
const COLOR = {
  internal: '#d7f1da',
  faceToFace: '#d2effd',
  online: '#d4d6fd',
  triple:
    'linear-gradient(to right, #d7f1da 0%,#d7f1da 33%,#000000 33%,#d2effd 33%,#d2effd 66%, #000000 66%, #d4d6fd 66%, #d4d6fd 100%)',
  taken: '#fde9cc',
  selected: 'lightgrey',
  default: '#eff1f2',
};

// Obtener la zona horaria del .env
const API_TIMEZONE = process.env.REACT_APP_API_TIMEZONE || 'America/Montevideo';

function DoctariCalendar({
  creatingAvailability,
  creatingAppointment,
  children,
  currentWeek,
  availability,
  setAvailability,
  schedules,
  deleteSchedule,
  isAssistant,
  isOperator,
  slot,
  isDoctor,
}) {
  const classes = useStyles();
  const isMobile = mobileScreen();
  const { start, end, isEditable } = availability || {};
  const theme = useTheme();
  const [selectedScheduleForAppointment, setSelectedScheduleForAppointment] =
    useState();
  const [
    selectedScheduleForAppointmentDate,
    setSelectedScheduleForAppointmentDate,
  ] = useState();
  const [modalOpen, setModalOpen] = useState({
    appointment: false,
    createSchedule: false,
    deleteSchedule: false,
    scheduleExistAppointment: false,
  });
  const [actionType, setActionType] = useState(null);

  const history = useHistory();

  // Función para formatear la fecha exactamente como vienen las keys del objeto schedules
  const formatScheduleKey = (date) => {
    // Mantener el offset original de la fecha
    return moment(date).format('YYYY-MM-DDTHH:mm:ssZ');
  };

  const selectDate = (date) => {
    if (isEditable) {
      if (start) {
        if (start === date) {
          setAvailability({
            ...availability,
            start: availability.end,
            end: null,
          });
        } else if (moment(start).isSame(moment(date), 'day')) {
          if (moment(start).diff(moment(date)) > 0) {
            setAvailability({
              ...availability,
              start: date,
              end: availability.start,
            });
          } else {
            setAvailability({ ...availability, end: date });
          }
        }
      } else {
        setAvailability({ ...availability, start: date });
      }
    } else {
      if (start === date) {
        setAvailability({ ...availability, start: null });
      } else {
        setAvailability({
          ...availability,
          start: date,
          createModalOpen: true,
        });
      }
    }
  };

  const selectScheduleForAppointment = (schedule, isForEdit) => {
    if (!isForEdit) {
      setModalOpen({ ...modalOpen, appointment: true });
    }
    setSelectedScheduleForAppointment(schedule);
    const scheduleDate = moment(schedule.start)
      .tz(API_TIMEZONE)
      .format('LL, h:mm: a');
    setSelectedScheduleForAppointmentDate(scheduleDate);
  };

  const isSelected = (date) => {
    let selected = false;
    if (start === date || end === date) {
      selected = true;
    } else if (start && end && start < date && date < end) {
      selected = true;
    }
    return selected;
  };

  // Función para buscar un schedule en el objeto schedules
  const findSchedule = (dateKey) => {
    if (!schedules) return null;

    // Buscar directamente con la clave exacta
    if (schedules[dateKey]) {
      return schedules[dateKey][0];
    }

    // Si no se encuentra, intentar buscar comparando solo la fecha y hora (ignorando el offset)
    const dateObj = moment(dateKey);
    for (const key of Object.keys(schedules)) {
      const keyDate = moment(key);
      if (dateObj.isSame(keyDate)) {
        return schedules[key][0];
      }
    }

    return null;
  };

  const Hours = ({ day }) => {
    const hours = getSlotsRange(slot);
    const hoursList = hours.map((hour, index) => {
      let currentScheduleAvailable = false;
      let chipColor = COLOR.default;

      // Crear la fecha del slot
      const slotDate = day
        .clone()
        .set('hour', parseInt(hour.slice(0, 2)))
        .set('minute', parseInt(hour.slice(3, 5)))
        .seconds(0)
        .milliseconds(0);

      // Formatear la fecha exactamente como vienen las keys del schedules
      const currentDateKey = formatScheduleKey(slotDate);
      
      // También mantener el formato anterior por compatibilidad (sin usar)
      // const currentDate = slotDate.format();

      // Buscar el schedule usando la función mejorada
      let currentSchedule = findSchedule(currentDateKey);

      // Calendar painting
      if (currentSchedule) {
        if (currentSchedule.taken === true) {
          // Taken schedule
          chipColor = COLOR.taken;
          currentScheduleAvailable = false;
        } else if (currentSchedule.types.length === 3) {
          // 3 schedules types painted with linear gradient defined as constant
          chipColor = COLOR.triple;
          currentScheduleAvailable = true;
        } else if (currentSchedule.types.length === 2) {
          // 2 schedules types, painted with linear gradient
          const type0 = currentSchedule.types[0];
          const type1 = currentSchedule.types[1];
          const color1 = type0 === SCHEDULE_TYPE.internal ? COLOR.internal : 
                         type0 === SCHEDULE_TYPE.online ? COLOR.online : COLOR.faceToFace;
          const color2 = type1 === SCHEDULE_TYPE.online ? COLOR.online : 
                         type1 === SCHEDULE_TYPE.internal ? COLOR.internal : COLOR.faceToFace;
          chipColor = `linear-gradient(to right, ${color1} 0%, ${color1} 50%, #000000 50%, ${color2} 50%, ${color2} 100%)`;
          currentScheduleAvailable = true;
        } else if (currentSchedule.types.length === 1) {
          // 1 schedule type
          chipColor =
            currentSchedule.types[0] === SCHEDULE_TYPE.internal
              ? COLOR.internal
              : currentSchedule.types[0] === SCHEDULE_TYPE.online
              ? COLOR.online
              : COLOR.faceToFace;
          currentScheduleAvailable = true;
        }
      }

      if (creatingAppointment) {
        // The calendar in which we can create appointment directly. The doctor's profile calendar.
        return (
          <Tooltip title={currentScheduleAvailable ? 'Click para agendar' : ''}>
            <Chip
              label={hour}
              className={classes.chip}
              style={{
                marginBottom: index + 1 === hours.length ? '1rem' : '0rem',
                background: isSelected(currentDateKey)
                  ? COLOR.selected
                  : chipColor,
                cursor: currentScheduleAvailable ? 'pointer' : 'default',
              }}
              onClick={() =>
                currentScheduleAvailable &&
                selectScheduleForAppointment(currentSchedule)
              }
              clickable={currentScheduleAvailable ? true : false}
              disabled={
                moment().tz(API_TIMEZONE).isSameOrAfter(currentDateKey) || chipColor === COLOR.taken
              }
            />
          </Tooltip>
        );
      } else {
        // Classic Doctari calendar. For editing it we have to go to edit mode. Doctor/assistant's home calendar.
        return (
          <>
            <Tooltip title={creatingAvailability ? 'Click para modificar' : ''}>
              <Chip
                label={hour}
                className={classes.chip}
                style={{
                  marginBottom: index + 1 === hours.length ? '1rem' : '0rem',
                  background: isSelected(currentDateKey)
                    ? COLOR.selected
                    : chipColor,
                  cursor: creatingAvailability ? 'pointer' : 'default',
                }}
                onClick={() => {
                  if (creatingAvailability && chipColor === COLOR.default) {
                    selectDate(currentDateKey);
                  } else if (creatingAvailability) {
                    setModalOpen({
                      ...modalOpen,
                      scheduleExistAppointment: isAssistant || isOperator,
                      deleteSchedule: isDoctor,
                    });
                    const foundSchedule = findSchedule(currentDateKey);
                    if (foundSchedule) {
                      selectScheduleForAppointment(foundSchedule, true);
                    }
                  }
                  chipColor = COLOR.default;
                }}
                clickable={false}
                disabled={
                  isAssistant
                    ? moment().tz(API_TIMEZONE).isSameOrAfter(currentDateKey) ||
                      chipColor === COLOR.taken ||
                      chipColor === COLOR.default
                    : moment().tz(API_TIMEZONE).isSameOrAfter(currentDateKey) ||
                      chipColor === COLOR.taken
                }
              />
            </Tooltip>
          </>
        );
      }
    });

    return hoursList;
  };

  const Days = () => {
    const calendarDay = [];
    for (let i = 0; i < 7; i++) {
      const currentDay = moment()
        .day(1)
        .add(currentWeek * 7 + i, 'days')
        .tz(API_TIMEZONE);
      
      const today = moment().tz(API_TIMEZONE);
      
      calendarDay.push(
        <div
          key={i}
          style={{
            border: 'solid 1px white',
            color: `${
              today.isSame(currentDay, 'day')
                ? theme.palette.primary.main
                : 'black'
            }`,
            fontWeight: `${
              today.isSame(currentDay, 'day') && 'bold'
            }`,
          }}
        >
          <p className={classes.dayName}>
            {capitalize(
              currentDay.format('ddd DD')
            )}
          </p>
        </div>
      );
    }
    return calendarDay;
  };

  const DaysHours = () => {
    const dayHoursList = [];
    for (let i = 0; i < 7; i++) {
      dayHoursList.push(
        <div
          key={i}
          className={classes.hourContainer}
          style={{
            backgroundColor: 'white',
          }}
        >
          <Hours
            day={moment()
              .day(1)
              .add(currentWeek * 7 + i, 'days')
              .tz(API_TIMEZONE)}
          />
        </div>
      );
    }
    return dayHoursList;
  };

  const ReferenceSection = () => {
    return (
      <div className={classes.referenceContainer}>
        <References />
        <Box>{children}</Box>
      </div>
    );
  };

  const handleAction = () => {
    if (actionType === 'deleteAvailability') {
      setModalOpen({
        ...modalOpen,
        scheduleExistAppointment: false,
        deleteSchedule: true,
      });
    } else {
      history.push({
        pathname: APPOINTMENT_LIGHT,
        state: { schedule: selectedScheduleForAppointment },
      });
    }
  };

  return (
    <Grid container style={{ marginBottom: '5rem' }}>
      <DoctariModal // create appointment modal
        open={modalOpen.appointment}
        setOpen={(val) => setModalOpen({ ...modalOpen, appointment: val })}
        title={'Agendar consulta'}
      >
        <div style={{ margin: 10 }}>
          <p style={{ margin: 10 }}>
            {`Agendar consulta para el ${selectedScheduleForAppointmentDate}`}
          </p>
          <Button
            color='primary'
            variant='contained'
            onClick={() =>
              history.push({
                pathname: APPOINTMENT,
                state: {
                  doctor: selectedScheduleForAppointment.doctor,
                  schedule: selectedScheduleForAppointment,
                  isAssistant,
                },
              })
            }
            style={{ float: 'right', marginBottom: 15 }}
          >
            Agendar
          </Button>
        </div>
      </DoctariModal>

      <DoctariModal
        open={modalOpen.scheduleExistAppointment}
        setOpen={(val) =>
          setModalOpen({ ...modalOpen, scheduleExistAppointment: val })
        }
        title={'Seleccione acción'}
      >
        <Box style={{ padding: '2rem' }}>
          <RadioGroup
            onChange={(e) => setActionType(e.target.value)}
            //   value={repeat}
            row
            style={{ justifyContent: 'space-around' }}
          >
            {!isAssistant && (
              <FormControlLabel
                value='deleteAvailability'
                control={<Radio color='primary' />}
                label='Eliminar disponibilidad'
              />
            )}

            <FormControlLabel
              value='appointment'
              control={<Radio color='primary' />}
              label='Agendar consulta'
            />
          </RadioGroup>

          <Grid
            item
            xs={12}
            md={12}
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '1rem',
            }}
          >
            <Button
              color='primary'
              variant='contained'
              disabled={!actionType}
              style={{ width: '200px' }}
              onClick={() => {
                handleAction();
              }}
            >
              Aceptar
            </Button>
          </Grid>
        </Box>
      </DoctariModal>

      <DoctariModal // deleting schedule modal
        open={modalOpen.deleteSchedule}
        setOpen={(val) => setModalOpen({ ...modalOpen, deleteSchedule: val })}
        title={'Editar Disponibilidad'}
      >
        <EditScheduleModal
          setModalOpen={setModalOpen}
          availabilityStart={start}
          availabilityEnd={end}
          setAvailability={setAvailability}
          selectedSchedule={selectedScheduleForAppointment}
          deleteSchedule={deleteSchedule}
          slot={slot}
        />
      </DoctariModal>
      <Grid
        item
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <div className={classes.weekContainer}>
          <div style={{ display: 'flex' }}>
            <Days />
          </div>

          <div className={classes.hourList}>
            <div style={{ display: 'flex' }}>
              <DaysHours />
            </div>
          </div>
          {!isMobile && <ReferenceSection />}
        </div>
        {isMobile && <ReferenceSection />}
      </Grid>
    </Grid>
  );
}

export default DoctariCalendar;